import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries La Grande-Motte' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à La Grande-Motte',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'La Grande-Motte' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à La Grande-Motte. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Les baies vitrées des résidences de La Grande-Motte résistent-elles au vent marin ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les constructions de La Grande-Motte intègrent de nombreuses baies vitrées. Avec les années, l\'air salin et les vents marins fragilisent les mécanismes. Lors de nos réparations, nous utilisons des pièces renforcées anticorrosion et des joints étanches.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pouvez-vous intervenir le week-end à La Grande-Motte ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons 7 jours sur 7. C\'est particulièrement utile pour les résidences secondaires dont les propriétaires sont présents le week-end. Nous pouvons convenir d\'un rendez-vous qui correspond à vos dates de présence.',
      },
    },
  ],
};

export default function LaGrandeMottePage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante La Grande-Motte | DFPRO"
        description="Baie vitrée exposée au vent, volet roulant bloqué à La Grande-Motte ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Pièces anticorrosion."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-la-grande-motte/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à La Grande-Motte"
        subtitle="Station balnéaire exposée aux vents marins : nos techniciens réparent baies vitrées, volets roulants et stores avec des pièces anticorrosion. 7j/7, devis gratuit."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Baie vitrée ou volet en panne à La Grande-Motte ?"
        ctaText="Devis gratuit, pièces anticorrosion, intervention 24-48h, 7j/7. Nos techniciens connaissent les spécificités des résidences du littoral."
        ctaServiceLabel="Découvrir nos prestations"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À La Grande-Motte, le vent et le sel sont les ennemis des menuiseries. Roulettes corrodées, volets bloqués par les rafales, vitrages embués : nous utilisons des pièces traitées anticorrosion. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/grandemotte-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à La Grande-Motte"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baies vitrées : roulettes et rails corrodés</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les baies vitrées sont omniprésentes dans les résidences de La Grande-Motte. Avec l'âge, les <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes et galets</Link> s'usent, le <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link> se corrode, la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Réglage</Link>, <Link to="/serrure-cremone-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">serrure</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link> complet. Pièces anticorrosion, garantie 10 ans.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant et store : face au vent et aux UV</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> subissent vent fort, embruns et soleil. <Link to="/volet-roulant-electrique-herault/" className="text-accent-500 font-semibold hover:underline">Électriques</Link>, <Link to="/volet-roulant-solaire-herault/" className="text-accent-500 font-semibold hover:underline">solaires</Link> : nous réparons tous les types.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Stores : <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">bannes</Link> aux toiles arrachées, <Link to="/motorisation-store-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> en panne. Nous remplaçons avec des toiles haute résistance UV.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/grandemotte-volet-02.jpg"
              alt="Réparation de volet roulant à La Grande-Motte"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/grandemotte-vitrage-03.avif"
              alt="Remplacement de vitrage à La Grande-Motte"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrage et fenêtre : étanchéité en bord de mer</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">Vitrages embués</Link> fréquents en front de mer : nous remplaçons par du <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> neuf. <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Urgence vitrier</Link> sous 24h.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> étanches, <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à La Grande-Motte sous 24 à 48h</h2>
        <p>
          Nous couvrons toute La Grande-Motte : centre-ville, le port, quartier du Ponant, le Couchant. Également Le Grau-du-Roi, Aigues-Mortes. Consultez <Link to="/depannage-menuiserie-mauguio/" className="text-accent-500 font-semibold hover:underline">Mauguio</Link> et <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}